<template>
  <div class="form-audio-input-wrapper">
    <FormLabel
      :element="element"
      :labelStyle="labelStyle"
      :fieldStyle="fieldStyle"
    />
    <FormDescription :element="element" :labelStyle="labelStyle" />
    <div class="audio-only-control">
      <button
        type="button"
        class="mic-button"
        :data-recording="isListening ? 'true' : 'false'"
        :aria-pressed="isListening ? 'true' : 'false'"
        :aria-label="isListening ? 'Stop recording' : 'Start recording'"
        :title="buttonTitle"
        :disabled="isButtonDisabled"
        @click="toggleRecording"
      >
        <span class="mic-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 15C13.6569 15 15 13.6569 15 12V6C15 4.34315 13.6569 3 12 3C10.3431 3 9 4.34315 9 6V12C9 13.6569 10.3431 15 12 15Z"
            />
            <path
              d="M18 11C18 14.3137 15.3137 17 12 17C8.68629 17 6 14.3137 6 11H4C4 15.0796 7.05817 18.4463 11 18.9444V22H13V18.9444C16.9418 18.4463 20 15.0796 20 11H18Z"
            />
          </svg>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import FormLabel from '@/components/FormBuilder/core/FormLabel.vue';
import FormDescription from '@/components/FormBuilder/core/FormDescription.vue';

export default {
  name: 'FormAudioInputElement',
  props: {
    element: {
      type: Object,
      required: true,
    },
    elements: {
      type: Array,
      default: () => [],
    },
    labelStyle: { type: Object, default: () => {} },
    fieldStyle: { type: Object, default: () => {} },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      isListening: false,
      recognition: null,
      silenceTimer: null,
      finalTranscript: '',
      interimTranscript: '',
      lastTranscript: '',
      hasSpeechSupport: false,
      targetInitialValue: '',
    };
  },
  computed: {
    isDisabled() {
      return this.disabled || this.element?.settings?.disabled;
    },
    targetElementName() {
      return this.element?.settings?.targetElementName || '';
    },
    isTargetAvailable() {
      return !!this.getTargetElement();
    },
    silenceTimeoutMs() {
      return Number(this.element?.settings?.silenceTimeoutMs) || 3000;
    },
    isButtonDisabled() {
      return (
        this.isDisabled || !this.isTargetAvailable || !this.hasSpeechSupport
      );
    },
    buttonTitle() {
      if (this.isListening) return 'Stop recording';
      if (!this.hasSpeechSupport)
        return 'Speech recognition is not supported in this browser';
      if (!this.isTargetAvailable)
        return 'Select a target text or textarea field in settings';
      if (this.isDisabled) return 'Audio is disabled';
      return 'Start recording';
    },
  },
  methods: {
    getSpeechRecognitionConstructor() {
      if (typeof window === 'undefined') return null;
      return window.SpeechRecognition || window.webkitSpeechRecognition || null;
    },
    getTargetElement() {
      if (!this.targetElementName) return null;
      return this.elements.find(
        (el) =>
          ['text', 'textarea'].includes(el.type) &&
          el.name === this.targetElementName
      );
    },
    setupRecognition() {
      const SR = this.getSpeechRecognitionConstructor();
      this.hasSpeechSupport = !!SR;
      if (!SR || this.recognition) return;

      const recognition = new SR();
      recognition.lang =
        this.element?.settings?.language ||
        (typeof navigator !== 'undefined' && navigator.language) ||
        'de-DE';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      recognition.addEventListener('start', () => {
        this.isListening = true;
        this.resetSilenceTimer();
      });
      recognition.addEventListener('end', () => {
        this.isListening = false;
        this.clearSilenceTimer();
      });
      recognition.addEventListener('error', () => {
        this.isListening = false;
        this.clearSilenceTimer();
      });
      recognition.addEventListener('result', this.handleResult);

      this.recognition = recognition;
    },
    toggleRecording() {
      if (this.isButtonDisabled) return;
      if (this.isListening) {
        this.stopRecording();
      } else {
        this.startRecording();
      }
    },
    startRecording() {
      this.setupRecognition();
      if (!this.recognition) return;

      this.finalTranscript = '';
      this.interimTranscript = '';
      const target = this.getTargetElement();
      this.targetInitialValue = target?.value || '';
      this.resetSilenceTimer();

      try {
        this.recognition.start();
      } catch (error) {
        this.isListening = false;
        this.clearSilenceTimer();
      }
    },
    stopRecording() {
      this.clearSilenceTimer();
      if (!this.recognition) return;
      try {
        this.recognition.stop();
      } catch (error) {
        this.isListening = false;
      }
    },
    handleResult(event) {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) {
          this.finalTranscript += `${result[0].transcript} `;
        } else {
          interim += result[0].transcript;
        }
      }

      this.interimTranscript = interim;
      this.resetSilenceTimer();

      const transcript =
        `${this.finalTranscript}${this.interimTranscript}`.trim();
      if (transcript) {
        this.lastTranscript = transcript;
        this.$set(this.element, 'value', transcript);
        const target = this.getTargetElement();
        if (target) {
          this.$set(
            target,
            'value',
            this.appendText(this.targetInitialValue, transcript)
          );
        }
      }
    },
    appendText(base, text) {
      const baseText = String(base || '').trimEnd();
      const appendText = String(text || '').trim();
      if (!appendText) return baseText;
      if (!baseText) return appendText;
      return `${baseText} ${appendText}`;
    },
    resetSilenceTimer() {
      this.clearSilenceTimer();
      this.silenceTimer = setTimeout(() => {
        if (this.isListening) this.stopRecording();
      }, this.silenceTimeoutMs);
    },
    clearSilenceTimer() {
      if (!this.silenceTimer) return;
      clearTimeout(this.silenceTimer);
      this.silenceTimer = null;
    },
  },
  mounted() {
    this.setupRecognition();
  },
  beforeDestroy() {
    this.clearSilenceTimer();
    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch (error) {
        // no-op
      }
    }
  },
  components: {
    FormLabel,
    FormDescription,
  },
};
</script>

<style scoped>
.form-audio-input-wrapper .audio-only-control {
  display: inline-flex;
  align-items: center;
}

.form-audio-input-wrapper .mic-button {
  position: relative;
  width: 65px;
  height: 65px;
  border-radius: 999px;
  border: 0.7px solid #dddddd;
  background: #fff;
  color: #111;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
  padding: 0;
  transform-origin: center;
}

.form-audio-input-wrapper .mic-button[data-recording='true'] {
  background: #111;
  color: #fff;
  animation: recPulse 1s ease-in-out infinite;
}

.form-audio-input-wrapper .mic-button::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.12);
  opacity: 0;
  transform: scale(1);
  pointer-events: none;
}

.form-audio-input-wrapper .mic-button[data-recording='true']::after {
  opacity: 1;
  animation: halo 1s ease-in-out infinite;
}

.form-audio-input-wrapper .mic-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.form-audio-input-wrapper .mic-icon svg {
  width: 100%;
  height: 100%;
  display: block;
}

@keyframes recPulse {
  0%,
  100% {
    transform: scale(1);
    filter: brightness(1);
  }
  50% {
    transform: scale(0.94);
    filter: brightness(0.9);
  }
}

@keyframes halo {
  0% {
    transform: scale(1);
    opacity: 0.22;
  }
  50% {
    transform: scale(1);
    opacity: 0.08;
  }
  100% {
    transform: scale(1);
    opacity: 0.22;
  }
}

.form-audio-input-wrapper .mic-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
</style>
